import { Component, computed, inject, signal, TemplateRef, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { take } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import {
  CustomCheckboxDialogComponent,
  NotificationService,
  SlideOverPanelService,
  PolicyCardConfig,
  SortDropdownComponent,
  SortOption,
  SortDirection,
  sortItems
} from '@htx/core';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { MatIcon } from '@angular/material/icon';
import {
  Policy,
  PolicyService,
  OdrlCategory,
  buildPolicyCardsForPolicy,
  buildRulePolicyCard,
  policyAutoName,
  policyAutoDescription
} from '@htx/policies';
import { AppButtonComponent } from '../../../../../core/buttons/app-button/app-button.component';
import { IntroductionBoxComponent } from '../../../../../core/introduction-box/introduction-box.component';
import { PolicyOverviewCardComponent } from '../policy-overview-card/policy-overview-card.component';

@Component({
  selector: 'app-policy-overview-page',
  imports: [TranslocoDirective, TranslocoPipe, MatIcon, AppButtonComponent, IntroductionBoxComponent, PolicyOverviewCardComponent, SortDropdownComponent],
  templateUrl: './policy-overview-page.component.html',
  styleUrl: './policy-overview-page.component.scss'
})
export class PolicyOverviewPageComponent {
  private readonly policyService = inject(PolicyService);
  private readonly translocoService = inject(TranslocoService);
  private readonly dialog = inject(MatDialog);
  private readonly notificationService = inject(NotificationService);
  private readonly panelService = inject(SlideOverPanelService);
  private readonly router = inject(Router);
  private readonly datePipe = new DatePipe('en-US');

  /** Sort configuration */
  private allPolicies = toSignal(this.policyService.getPolicies$(), { initialValue: [] as Policy[] });

  sortOptions: SortOption<Policy>[] = [
    { field: 'createdAt', labelKey: 'sort.field.createdAt' },
    { field: (p: Policy) => p.privateProperties?.name ?? '', labelKey: 'sort.field.name' },
    { field: 'id', labelKey: 'sort.field.id' }
  ];
  sortField = signal<SortOption<Policy>['field']>(this.sortOptions[0].field);
  sortDirection = signal<SortDirection>('desc');

  sortedPolicies = computed(() =>
    sortItems(this.allPolicies(), { field: this.sortField(), direction: this.sortDirection() })
  );

  onSortChanged(event: { field: SortOption<Policy>['field']; direction: SortDirection }): void {
    this.sortField.set(event.field);
    this.sortDirection.set(event.direction);
  }

  /** Template refs for the slide-over panel */
  detailBodyTpl = viewChild.required<TemplateRef<unknown>>('panelBody');
  headerActionsTpl = viewChild.required<TemplateRef<unknown>>('panelHeaderActions');

  /** State for the currently previewed policy in the panel */
  panelPolicy = signal<Policy | null>(null);

  /** Auto-generated or custom description */
  panelDescription = computed(() => {
    const p = this.panelPolicy();
    if (!p) return '';
    return policyAutoDescription(this.translocoService, p);
  });

  /** Formatted creation date */
  panelCreatedAt = computed(() => {
    const p = this.panelPolicy();
    if (!p?.createdAt) return '';
    return this.datePipe.transform(p.createdAt, 'dd.MM.yyyy HH:mm') ?? '';
  });

  /** Permission policy cards */
  panelPermissionCards = computed((): PolicyCardConfig[] => {
    const p = this.panelPolicy();
    if (!p) return [];
    const rules = p.rules ?? [];
    const permissions = rules.filter(r => r.category === OdrlCategory.PERMISSION);
    if (permissions.length > 0) {
      return permissions.map(r => buildRulePolicyCard(this.translocoService, r));
    }
    // Fallback: flat constraints shown as permission cards
    return buildPolicyCardsForPolicy(this.translocoService, p);
  });

  /** Obligation policy cards */
  panelObligationCards = computed((): PolicyCardConfig[] => {
    const p = this.panelPolicy();
    if (!p) return [];
    const rules = p.rules ?? [];
    return rules
      .filter(r => r.category === OdrlCategory.OBLIGATION)
      .map(r => buildRulePolicyCard(this.translocoService, r));
  });

  /** Prohibition policy cards */
  panelProhibitionCards = computed((): PolicyCardConfig[] => {
    const p = this.panelPolicy();
    if (!p) return [];
    const rules = p.rules ?? [];
    return rules
      .filter(r => r.category === OdrlCategory.PROHIBITION)
      .map(r => buildRulePolicyCard(this.translocoService, r));
  });

  openPolicyDetailPage(policy: Policy): void {
    this.panelPolicy.set(null);

    this.panelService.open(
      {
        title: policyAutoName(this.translocoService, policy),
        subtitle: `ID: ${policy.id}`,
        initialWidth: 560
      },
      {
        bodyTemplate: this.detailBodyTpl(),
        headerActionsTemplate: this.headerActionsTpl(),
        context: policy
      }
    );

    this.panelPolicy.set(policy);
  }

  deletePolicyFromPanel(): void {
    const p = this.panelPolicy();
    if (p) this.deleteCurrentPolicy(p);
  }

  editPolicyFromPanel(): void {
    const p = this.panelPolicy();
    if (!p) return;
    this.panelService.close();
    void this.router.navigate(['/policies/edit', p.id]);
  }

  openCreatePolicyPage(): void {
    void this.router.navigate(['/policies/create']);
  }

  deleteCurrentPolicy(policy: Policy): void {
    this.dialog
      .open(CustomCheckboxDialogComponent, {
        data: {
          title: this.translocoService.translate('policy.customCheckbox.title'),
          description: this.translocoService.translate('policy.customCheckbox.description', {
            policy: { id: policy.id }
          }),
          checkboxDescription: this.translocoService.translate(
            'policy.customCheckbox.checkboxDescription'
          ),
          confirmButtonLable: this.translocoService.translate('dialog.button.delete.label')
        }
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        if (result) {
          this.policyService
            .deletePolicy(policy.id)
            .pipe(take(1))
            .subscribe({
              next: () => {
                const currentPolicies = this.policyService.policiesSubject.getValue();
                const updatedPolicies = currentPolicies.filter((p) => p.id !== policy.id);
                this.policyService.policiesSubject.next(updatedPolicies);
                this.panelService.close();
                this.notificationService.showSuccess(
                  this.translocoService.translate('policy.deletion.successMessage', {
                    policy: { id: policy.id }
                  })
                );
              },
              error: (error) => {
                if (error.status === 409) {
                  this.notificationService.showError(
                    this.translocoService.translate('policy.deletion.errorMessage', {
                      policy: { id: policy.id }
                    }),
                    5000
                  );
                }
              }
            });
        }
      });
  }
}
